import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useMkRoot } from "@/app/branding";

import { MkSpinner } from "./MkSpinner";
import type { RelRecord } from "./useRelSearch";

const MARGIN = 8;
const MIN_WIDTH = 260;
const MAX_HEIGHT = 340;
const MIN_HEIGHT = 160;

interface Props {
    open: boolean;
    anchor(): HTMLElement | null;
    items?: RelRecord[];
    active?: number;
    loading?: boolean;
    multi?: boolean;
    selectedIds?: number[];
    emptyLabel?: string;
    createLabel?: string;
    moreLabel?: string;
    hintLabel?: string;
    onPick(item: RelRecord): void;
    onHover?(index: number): void;
    onCreate?(): void;
    onMore?(): void;
}

export function MkRelDropdown({ open, anchor, items = [], active = -1, loading, multi,
                                selectedIds = [], emptyLabel, createLabel, moreLabel, hintLabel,
                                onPick, onHover, onCreate, onMore }: Props) {
    const root = useMkRoot();
    const panel = useRef<HTMLDivElement>(null);

    const place = useCallback(() => {
        const node = panel.current;
        const target = anchor();
        if (!node || !target) {
            return;
        }
        const rect = target.getBoundingClientRect();
        const width = Math.min(Math.max(rect.width, MIN_WIDTH), window.innerWidth - MARGIN * 2);
        node.style.width = `${width}px`;
        const below = window.innerHeight - rect.bottom - MARGIN;
        const above = rect.top - MARGIN;
        const up = below < Math.min(MIN_HEIGHT, node.scrollHeight) && above > below;
        const room = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, up ? above : below));
        node.style.maxHeight = `${room}px`;
        const height = node.offsetHeight;
        let left = rect.left;
        left = Math.min(left, window.innerWidth - width - MARGIN);
        node.style.left = `${Math.max(MARGIN, left)}px`;
        node.style.top = up ? `${Math.max(MARGIN, rect.top - height - 6)}px` : `${rect.bottom + 6}px`;
        node.classList.toggle("is-up", up);
    }, [anchor]);

    useLayoutEffect(() => {
        if (open) {
            place();
        }
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [open, place]);

    if (!open) {
        return null;
    }

    return createPortal(
        <div className="mk mk-rel-pop" ref={panel} {...root}
             onMouseDown={(event) => event.preventDefault()}>
            {loading && !items.length ? (
                <div className="mk-rel-loading">
                    <MkSpinner size="sm" />
                </div>
            ) : (
                <>
                    <div className="mk-rel-list">
                        {items.map((item, index) => {
                            const picked = selectedIds.includes(item.id);
                            return (
                                <button key={item.id} type="button"
                                        className={`mk-rel-opt ${index === active ? "is-active" : ""} ${picked ? "is-picked" : ""}`}
                                        onMouseEnter={() => onHover?.(index)}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            onPick(item);
                                        }}>
                                    <i className={`fa ${multi ? (picked ? "fa-check-square-o" : "fa-square-o") : "fa-circle-o"}`} />
                                    <span className="mk-rel-opt-name">{item.name}</span>
                                </button>
                            );
                        })}
                        {!items.length && !createLabel && (
                            <div className="mk-rel-empty">{emptyLabel}</div>
                        )}
                    </div>
                    {(createLabel || moreLabel) && (
                        <div className="mk-rel-foot">
                            {createLabel && (
                                <button type="button" className="mk-rel-foot-btn is-create"
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            onCreate?.();
                                        }}>
                                    <i className="fa fa-plus" />
                                    <span>{createLabel}</span>
                                </button>
                            )}
                            {moreLabel && (
                                <button type="button" className="mk-rel-foot-btn is-more"
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            onMore?.();
                                        }}>
                                    <i className="fa fa-search-plus" />
                                    <span>{moreLabel}</span>
                                </button>
                            )}
                        </div>
                    )}
                    {hintLabel && items.length > 0 && <div className="mk-rel-hint">{hintLabel}</div>}
                </>
            )}
        </div>,
        document.body,
    );
}
